import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import React, { ReactNode, useState } from 'react';
import Utilities from '@util/Utilities';
import _isEqual from 'lodash/isEqual';

export type MenuButtonFilterItem<T = any> = {
	name: string;
	description?: string;
	value: T | undefined;
	icon?: ReactNode;
	disabled?: boolean;
	props?: any
};

type MenuButtonFilterProps<T = any> = {
	/** Array of menu items to display */
	menuItems: MenuButtonFilterItem<T | undefined>[];

	/** Currently selected value (single select) or array of selected values (multi select) */
	selectedValue?: T | undefined;

	/** Callback when selection changes (single select) */
	onSelect?: (value: T | undefined) => void;

	/** Enable multi-select mode */
	enableMultiSelect?: boolean;

	/** Selected values for multi-select mode */
	selectedValues?: T[];

	/** Callback when multi-selection changes */
	onMultiSelect?: (values: T[]) => void;

	/** Optional max number of selections allowed in multi-select mode */
	maxMultiSelect?: number;

	/** Custom display function for multi-select */
	multiSelectDisplayText?: (selectedItems: MenuButtonFilterItem<T>[]) => string;

	/** Optional description text shown in menu header */
	menuDescription?: string;

	/** Optional custom display function for button text */
	displayText?: (selectedItem: MenuButtonFilterItem<T | undefined>) => string;

	/** Optional custom ReactNode to override the button's selected item display */
	displaySelectedItem?: (selectedItem: MenuButtonFilterItem<T | undefined> | undefined) => ReactNode;

	/** Optional custom render function for menu items */
	displayItem?: (item: MenuButtonFilterItem<T | undefined>) => ReactNode;

	/** Optional custom comparison function for determining selected item */
	isSelected?: (itemValue: T | undefined, selectedValue: T | undefined) => boolean;

	/** Optional custom function to determine if filter is active (for styling) */
	isActive?: (selectedValue: T | undefined) => boolean;

	/** Optional custom button className */
	buttonClassName?: string;

	/** Optional menu anchor position */
	menuAnchor?: 'bottom start' | 'bottom end' | 'top start' | 'top end';

	/** Optional menu width */
	menuWidth?: string;

	/** Optional button height */
	buttonHeight?: string;

	/** Optional text size */
	textSize?: string;

	/** Optional disabled state */
	disabled?: boolean;

	/** Optional enable search/filter input */
	enableSearch?: boolean;

	/** Optional search placeholder text */
	searchPlaceholder?: string;

	/** Optional footer content to display at the bottom of the menu */
	footer?: ReactNode;
};

const MenuButtonFilter = <T = any, >({
  menuItems,
  selectedValue = undefined,
  onSelect,
  enableMultiSelect = false,
  selectedValues = [],
  onMultiSelect,
  maxMultiSelect,
  multiSelectDisplayText,
  menuDescription,
  displayText,
  displaySelectedItem,
  displayItem,
  isSelected: customIsSelected,
  isActive: customIsActive,
  buttonClassName,
  menuAnchor = 'bottom end',
  menuWidth = 'w-56',
  buttonHeight = 'h-8',
  textSize = 'text-xs',
  disabled = false,
  enableSearch = false,
  searchPlaceholder = 'Filter',
  footer,
}: MenuButtonFilterProps<T>): React.ReactElement => {

  const [searchTerm, setSearchTerm] = useState('');

  // Default comparison function - handles null/undefined equality and deep object comparison
  const defaultIsSelected = (itemValue: T | undefined, selectedValue: T | undefined): boolean => {
    // Handle null/undefined as equivalent
    if ((itemValue === null || itemValue === undefined) && (selectedValue === null || selectedValue === undefined)) {
      return true;
    }
    // Use deep equality for objects
    return _isEqual(itemValue, selectedValue);
  };

  const isSelectedFn = customIsSelected || defaultIsSelected;

  // Default active state detection - active when a value is selected and not any
  const defaultIsActive = (selectedValue: T | undefined): boolean => {
    return selectedValue !== undefined;
  };

  const isActiveFn = customIsActive || defaultIsActive;

  // Multi-select helper functions
  const isItemSelectedInMultiSelect = (itemValue: T | undefined): boolean => {
    if (!enableMultiSelect || !selectedValues) return false;
    return selectedValues.some(val => _isEqual(val, itemValue));
  };

  const handleMultiSelectToggle = (value: T | undefined) => {
    if (!onMultiSelect || !selectedValues) return;

    const isCurrentlySelected = isItemSelectedInMultiSelect(value);
    let newValues: T[];

    if (isCurrentlySelected) {
      // Remove from selection
      newValues = selectedValues.filter(val => !_isEqual(val, value));
    } else {
      // Add to selection (filter out undefined)
      if (value !== undefined) {
        if (maxMultiSelect !== undefined && selectedValues.length >= maxMultiSelect) {
          return;
        }
        newValues = [...selectedValues, value];
      } else {
        newValues = selectedValues;
      }
    }

    onMultiSelect(newValues);
  };

  const selectedItem = (): MenuButtonFilterItem<T | undefined> | undefined => {
    return menuItems.find(item => isSelectedFn(item.value, selectedValue));
  };

  const selectedItems = (): MenuButtonFilterItem<T>[] => {
    if (!enableMultiSelect || !selectedValues) return [];
    return menuItems.filter(item =>
      item.value !== undefined && selectedValues.some(val => _isEqual(val, item.value)),
    ) as MenuButtonFilterItem<T>[];
  };

  const display = (): string => {
    if (enableMultiSelect) {
      const selected = selectedItems();

      if (multiSelectDisplayText) {
        return multiSelectDisplayText(selected);
      }

      if (selected.length === 0) return 'Select...';
      if (selected.length === 1) return selected[0].name;
      return `${selected.length} selected`;
    }

    const selected = selectedItem();
    if (!selected) return 'Select...';

    if (displayText) {
      return displayText(selected);
    }

    return selected.name;
  };

  const handleSelect = (value: T | undefined) => {
    if (enableMultiSelect) {
      handleMultiSelectToggle(value);
      // Don't clear search in multi-select mode to allow multiple selections
    } else {
      onSelect?.(value);
      setSearchTerm(''); // Clear search after selection
    }
  };

  const isFilterActive = enableMultiSelect
    ? (selectedValues && selectedValues.length > 0)
    : isActiveFn(selectedValue);

  const isMaxMultiSelectReached = enableMultiSelect
    && maxMultiSelect !== undefined
    && selectedValues
    && selectedValues.length >= maxMultiSelect;

  // Filter menu items based on search term
  const filteredMenuItems = enableSearch && searchTerm
    ? menuItems.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    : menuItems;

  return (
    <Menu as="div" className="relative">
      <MenuButton
        type="button"
        disabled={disabled}
        className={Utilities.classNames(
          buttonClassName || `btn btn-light ${textSize} ${buttonHeight}`,
          isFilterActive && !disabled ? 'bg-blue-50 border-blue-400 text-blue-700' : '',
          disabled ? 'btn-disabled cursor-not-allowed' : '',
        )}
      >
        {displaySelectedItem ? (
          <>
            {displaySelectedItem(selectedItem())}
            <ChevronDownIcon
              className={Utilities.classNames(
                '-mr-1 ml-1 h-5 w-5',
                isFilterActive && !disabled ? 'text-blue-500' : 'text-gray-400',
              )}
            />
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              {selectedItem()?.icon && (
                <span className="flex-shrink-0">{selectedItem()!.icon}</span>
              )}
              <span>{display()}</span>
            </div>
            <ChevronDownIcon
              className={Utilities.classNames(
                '-mr-1 ml-1 h-5 w-5',
                isFilterActive && !disabled ? 'text-blue-500' : 'text-gray-400',
              )}
            />
          </>
        )}
      </MenuButton>

      <MenuItems
        anchor={menuAnchor}
        transition
        className={`z-10 mt-3 ${menuWidth} origin-top-right overflow-hidden rounded-md bg-white divide-y divide-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
      >
        {menuDescription && (
          <div className="px-4 py-3">
            <p className={`${textSize} text-gray-500`}>{menuDescription}</p>
          </div>
        )}

        {enableSearch && (
          <div className="px-4 py-3">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="py-1 max-h-64 overflow-y-auto">
          {filteredMenuItems.length === 0 && enableSearch && searchTerm ? (
            <div className="px-4 py-2 text-sm text-gray-500 text-center">
              No results found
            </div>
          ) : (
            filteredMenuItems.map((item, idx) => {
              const isSelectedInMulti = enableMultiSelect ? isItemSelectedInMultiSelect(item.value) : false;
              const isItemDisabled = !!item.disabled || (isMaxMultiSelectReached && enableMultiSelect && !isSelectedInMulti);

              return (
                <MenuItem key={`menu_item_${idx}`} disabled={isItemDisabled}>
                  {({ focus }) => (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isItemDisabled) {
                          handleSelect(item.value);
                        }
                      }}
                      className={Utilities.classNames(
                        isItemDisabled ? 'text-gray-400 cursor-not-allowed' : (focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'),
                        `relative block px-4 py-2 ${textSize}`,
                      )}
                    >
                      {displayItem ? displayItem(item) : (
                        <div className="flex items-center">
                          {enableMultiSelect && (
                            <span className={Utilities.classNames(
                              'mr-2 flex h-4 w-4 items-center justify-center rounded border',
                              isItemDisabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white',
                            )}>
                              {isSelectedInMulti && (
                                <CheckIcon className="h-4 w-4 text-green-500" />
                              )}
                            </span>
                          )}
                          {item.icon && <span className="mr-2">{item.icon}</span>}
                          <div className="flex-1">
                            <span
                              className={Utilities.classNames('font-semibold block', isItemDisabled ? 'text-gray-400' : 'text-gray-900')}>
                              {item.name}
                            </span>
                            {item.description && (
                              <span className="text-gray-500 text-xs block mt-0.5">
                                {item.description}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {!isItemDisabled && !enableMultiSelect && isSelectedFn(item.value, selectedValue) && (
                        <span className="text-indigo-600 absolute inset-y-0 right-0 flex items-center pr-4">
                          <CheckIcon className="h-5 w-5 text-green-500" />
                        </span>
                      )}
                    </a>
                  )}
                </MenuItem>
              );
            })
          )}
        </div>

        {footer && (
          <div className="px-4 py-3">
            {footer}
          </div>
        )}
      </MenuItems>
    </Menu>
  );
};

export default MenuButtonFilter;